import { clearAuthToken, setAuthToken } from "./deploymentConfig";
import { localActivationService, normalizeActivationHospital } from "./localActivationService";
import { localApiClient } from "./localApiClient";
import { localPermissionService } from "./permissionLocalApiService";

const LOCAL_SESSION_KEY = "clearhealthLocalApiSession";

const storage = () => (typeof window === "undefined" ? null : window.localStorage);

const normalizeHospital = (hospital = {}, fallbackHospitalId = "") => ({
  ...hospital,
  id: hospital.id || hospital.hospitalId || fallbackHospitalId,
  hospitalId: hospital.hospitalId || fallbackHospitalId,
  hospitalName: hospital.hospitalName || hospital.name || "ClearHealth Local Hospital",
  name: hospital.name || hospital.hospitalName || "ClearHealth Local Hospital",
  deploymentMode: hospital.deploymentMode || "OFFLINE",
});

const normalizeLocalUser = (user = {}, hospitalId = "", permissionsData = {}) => {
  const permissions = permissionsData.permissions || user.permissions || [];
  return {
    ...user,
    id: user.id,
    authUid: user.id,
    email: user.email,
    name: user.name || user.displayName || user.email,
    displayName: user.displayName || user.name || user.email,
    hospitalId: user.hospitalId || hospitalId,
    roleName: user.roleName || user.roles?.[0] || "Local User",
    roles: user.roles || (user.roleName ? [user.roleName] : []),
    permissions,
    permissionsV2: permissionsData.permissionsV2 || user.permissionsV2 || null,
    isLocalApiUser: true,
    authProvider: "local-api",
  };
};

export const storeLocalSession = (session) => {
  storage()?.setItem(LOCAL_SESSION_KEY, JSON.stringify(session || null));
};

export const getStoredLocalSession = () => {
  try {
    const raw = storage()?.getItem(LOCAL_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearLocalSession = () => {
  storage()?.removeItem(LOCAL_SESSION_KEY);
  clearAuthToken();
};

export const loginLocalApi = async (emailOrPhone, password, hospitalId, options = {}) => {
  try {
    let normalizedHospitalId = String(hospitalId || "").trim().toUpperCase();
    let activatedHospital = null;
    if (!normalizedHospitalId) {
      const status = await localActivationService.status();
      if (!status?.activated || !status?.hospitalId) {
        throw new Error("Local server is not activated. Activate ClearHealth Local Server before login.");
      }
      activatedHospital = normalizeActivationHospital(status);
      normalizedHospitalId = String(status.hospitalId || "").trim().toUpperCase();
    }
    const result = await localApiClient.post("/auth/login", {
      hospitalId: normalizedHospitalId,
      email: emailOrPhone,
      password,
    });
    if (result?.accessToken) {
      setAuthToken(result.accessToken, { persist: options.persist !== false });
    }

    const [hospital, permissionsData] = await Promise.all([
      localApiClient.get(`/hospitals/${encodeURIComponent(normalizedHospitalId)}`),
      localPermissionService.getCurrentUserPermissions(),
    ]);
    const localHospital = normalizeHospital(hospital || activatedHospital, normalizedHospitalId);
    const localUser = normalizeLocalUser(result.user, normalizedHospitalId, permissionsData);
    const session = {
      token: result.accessToken,
      user: localUser,
      hospitalId: normalizedHospitalId,
      hospital: localHospital,
      permissions: permissionsData?.permissions || [],
      permissionsV2: permissionsData?.permissionsV2 || null,
      loggedInAt: new Date().toISOString(),
      authProvider: "local-api",
    };
    if (options.persist !== false) storeLocalSession(session);
    return session;
  } catch (error) {
    const message = error?.message || String(error);
    if (/failed to fetch|network/i.test(message)) {
      throw new Error("Local API is unavailable. Check that the local server is running and reachable.");
    }
    if (/unauthor|invalid credentials|jwt|bearer/i.test(message)) {
      throw new Error("Invalid local login credentials.");
    }
    throw error;
  }
};

export const localSessionStorageKey = LOCAL_SESSION_KEY;
